/**
 * Contact Info Section
 * Fetches office information and contact details from CMS API
 */
import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Globe
} from 'lucide-react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const EMAIL_CATEGORY_ID = 53;
const PHONE_CATEGORY_ID = 54;
const SOCIAL_CATEGORY_ID = 61;
const OPEN_HOURS_SECTION_UID = 'OPEN-HOURS-SECTION';
const ADDRESS_SECTION_UID = 'ADDRESS-SECTION';
const CONTACT_SECTION_UID = 'CONTACT-SECTION';

// Parse contact item HTML to array of lines
const parseContactContent = (html, title) => {
  if (!html) return [];
  const lines = html
    .replace(/&nbsp;/g, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .split('\n')
    .map(s => s.trim())
    .filter(s => s.length > 0);

  // Skip first line if it matches the title (duplicate h3 heading)
  if (title && lines.length > 0 && lines[0].toLowerCase() === title.toLowerCase()) {
    return lines.slice(1);
  }
  return lines;
};

const lat = -6.8244097;
const lng = 39.2806425;

const ContactInfo = () => {
  const [sectionData, setSectionData] = useState(null);
  const [contactDetails, setContactDetails] = useState([]);
  const [socials, setSocials] = useState([]);
  const [addressSection, setAddressSection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch section title
        const sectionResponse = await axios.post(
          `${CMS_API_URL}sections/get-by-uid`,
          { UID: CONTACT_SECTION_UID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (sectionResponse.data?.code === 1 && Array.isArray(sectionResponse.data?.data) && sectionResponse.data.data.length > 0) {
          const data = sectionResponse.data.data[0];
          if (data.IsDeleted === false && data.IsPublished === true) {
            setSectionData({
              Title: data.SubTitle || 'Contact Information',
              SubTitle: data.Title,
            });
          }
        }

        // Fetch all contact sources in parallel
        const [emailsRes, phonesRes, openHoursRes, addressRes, socialsRes] = await Promise.allSettled([
          axios.post(
            `${CMS_API_URL}info/by-category`,
            { CategoryId: EMAIL_CATEGORY_ID },
            { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
          ),
          axios.post(
            `${CMS_API_URL}info/by-category`,
            { CategoryId: PHONE_CATEGORY_ID },
            { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
          ),
          axios.post(
            `${CMS_API_URL}sections/get-by-uid`,
            { UID: OPEN_HOURS_SECTION_UID },
            { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
          ),
          axios.post(
            `${CMS_API_URL}sections/get-by-uid`,
            { UID: ADDRESS_SECTION_UID },
            { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
          ),
          axios.post(
            `${CMS_API_URL}info/by-category`,
            { CategoryId: SOCIAL_CATEGORY_ID },
            { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
          ),
        ]);

        const parseInfoItems = (res) => {
          if (res.status === 'fulfilled' && res.value.data?.code === 1 && Array.isArray(res.value.data?.data)) {
            return res.value.data.data.filter(item => item.IsDeleted === false && item.IsPublished === true);
          }
          return [];
        };

        const parseSocialItems = (res) => {
          if (res.status === 'fulfilled' && res.value.data?.code === 1 && Array.isArray(res.value.data?.data)) {
            return res.value.data.data.filter(item => item.IsDeleted === false);
          }
          return [];
        };

        const emails = parseInfoItems(emailsRes);
        const phones = parseInfoItems(phonesRes);
        const socials = parseSocialItems(socialsRes);

        let address = null;
        if (addressRes.status === 'fulfilled' && addressRes.value.data?.code === 1 && Array.isArray(addressRes.value.data?.data) && addressRes.value.data.data.length > 0) {
          const addr = addressRes.value.data.data[0];
          if (addr.IsDeleted === false && addr.IsPublished === true) {
            address = addr;
            setAddressSection(addr);
          }
        }

        let openHoursSection = null;
        if (openHoursRes.status === 'fulfilled' && openHoursRes.value.data?.code === 1 && Array.isArray(openHoursRes.value.data?.data) && openHoursRes.value.data.data.length > 0) {
          const oh = openHoursRes.value.data.data[0];
          if (oh.IsDeleted === false && oh.IsPublished === true) {
            openHoursSection = oh;
          }
        }

        const details = [];

        // Address card
        if (address) {
          details.push({
            icon: MapPin,
            title: address.Title || 'Office Address',
            content: parseContactContent(address.Description, address.Title),
          });
        }

        // Phone card
        if (phones.length > 0) {
          const phoneLines = phones.map(p => {
            const desc = p.Description ? p.Description.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim() : '';
            return desc || p.Link || p.SubTitle || '';
          }).filter(Boolean);
          details.push({
            icon: Phone,
            title: phones[0].Title || 'Phone Numbers',
            content: phoneLines,
          });
        }

        // Email card
        if (emails.length > 0) {
          const emailLines = emails.map(e => {
            const desc = e.Description ? e.Description.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim() : '';
            return desc || e.Link || e.SubTitle || '';
          }).filter(Boolean);
          details.push({
            icon: Mail,
            title: emails[0].Title || 'Email Addresses',
            content: emailLines,
          });
        }

        // Open Hours card
        if (openHoursSection) {
          details.push({
            icon: Clock,
            title: openHoursSection.Title,
            content: parseContactContent(openHoursSection.Description, openHoursSection.Title),
          });
        }

        setContactDetails(details);
        setSocials(socials);
      } catch (error) {
        console.error('Failed to fetch contact info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (!loading && contactDetails.length === 0 && !sectionData) {
    return null;
  }

  if (loading) {
    return (
      <section className="py-8 bg-white">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-pulse" />
              <div className="h-8 w-64 bg-gray-200 rounded mb-4 animate-pulse" />
              <div className="w-16 h-1 bg-gray-200 mb-8 animate-pulse" />
              <div className="grid sm:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="flex items-start">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg mr-4 animate-pulse" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-gray-200 rounded mb-2 animate-pulse" />
                      <div className="h-3 w-48 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="h-[450px] bg-gray-200 rounded-2xl animate-pulse" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12">

          {/* LEFT - CONTACT DETAILS */}
          <div>
            {sectionData?.SubTitle && (
              <span className="text-yellow-600 font-semibold text-sm uppercase tracking-wide">
                {sectionData.SubTitle}
              </span>
            )}

            {sectionData?.Title && (
              <h2 className="text-3xl font-bold text-[#2f3951] mt-2 mb-4">
                {sectionData.Title}
              </h2>
            )}

            <div className="w-16 h-1 bg-yellow-500 mb-8"></div>
              {/* contact details */}
            <div className="grid sm:grid-cols-2 gap-8">
              {contactDetails.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-start">
                    <div className="w-12 h-12 bg-[#2f3951] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <Icon className="w-6 h-6 text-yellow-500" />
                    </div>

                    <div>
                      <h3 className="font-bold text-[#2f3951] mb-2">
                        {item.title}
                      </h3>

                      {item.content.map((line, i) => (
                        <p key={i} className="text-gray-600 text-sm">
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {socials.length > 0 && (
              <div className="mt-8">
                <h3 className="font-bold text-[#2f3951] mb-4">
                  {socials[0]?.Title}
                </h3>

                <div className="flex space-x-3">
                  {socials.map((social, index) => (
                    <a
                      key={index}
                      href={social.Link || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative w-10 h-10 bg-gray-100 hover:bg-[#2f3951] rounded-full flex items-center justify-center transition-colors group"
                      aria-label={social.Title}
                      title={social.Title}
                    >
                      <img
                        src={social.Image}
                        alt={social.Title}
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 object-contain transition-opacity group-hover:opacity-0"
                      />
                      <div
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity bg-[#EAB30A]"
                        style={{
                          WebkitMaskImage: `url(${social.Image})`,
                          maskImage: `url(${social.Image})`,
                          WebkitMaskSize: 'contain',
                          maskSize: 'contain',
                          WebkitMaskRepeat: 'no-repeat',
                          maskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'center',
                          maskPosition: 'center',
                        }}
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT - GOOGLE MAP FULL FEATURES */}
          <div className="rounded-2xl overflow-hidden shadow-lg bg-white">

            {/* MAP IFRAME */}
            <iframe
              title="Office Location Map"
              src={`https://www.google.com/maps?q=${lat},${lng}&hl=en&z=16&output=embed`}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-[450px]"
            ></iframe>

            {/* MAP ACTIONS */}
<div className="p-3 border-t bg-gray-50">
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full">

    {/* Open in Google Maps */}
    <a
      href={`https://www.google.com/maps?q=${lat},${lng}`}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full text-center px-2 py-2 text-xs font-medium bg-[#2f3951] text-white rounded-md hover:bg-[#1f2937] transition"
    >
      Open Map
    </a>

    {/* Directions */}
    <a
      href={addressSection?.Link || `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full text-center px-2 py-2 text-xs font-medium bg-[#2f3951] text-white rounded-md hover:bg-[#1f2937] transition"
    >
      Directions
    </a>

    {/* Street View */}
    <a
      href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full text-center px-2 py-2 text-xs font-medium bg-[#2f3951] text-white rounded-md hover:bg-[#1f2937] transition"
    >
      Street View
    </a>

    {/* Satellite View */}
    <a
      href={`https://www.google.com/maps?q=${lat},${lng}&t=k`}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full text-center px-2 py-2 text-xs font-medium bg-[#2f3951] text-white rounded-md hover:bg-[#1f2937] transition"
    >
      Satellite
    </a>

  </div>
</div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ContactInfo;